import { uid } from '../audio/decode';
import type { Division, Pattern, Project, Scene, Step, Track } from '../types';

export const TRACK_COLORS = ['#ff2d95', '#38bdf8', '#fbbf24', '#5eead4'];

export function makeStep(slice: number): Step {
  return {
    id: uid('st'),
    slice,
    mute: false,
    reverse: false,
    gain: 1,
    pan: 0,
    pitch: 0,
    gate: 1,
    repeat: 1,
    repeatRamp: 0,
    probability: 1,
    nudge: 0,
    rate: 1,
    filter: 'off',
    cutoff: 12000,
    resonance: 1,
  };
}

/** 1:1 pattern — step n plays slice n. The identity chop. */
export function makeLinearPattern(
  name: string,
  length: number,
  division: Division,
  color: string,
): Pattern {
  return {
    id: uid('pat'),
    name,
    length,
    division,
    swing: 0,
    color,
    steps: Array.from({ length }, (_, i) => makeStep(i)),
  };
}

export function makeTrack(index: number): Track {
  return {
    id: uid('trk'),
    name: `Track ${index + 1}`,
    sourceId: null,
    patternId: null,
    volume: 0.85,
    pan: 0,
    mute: false,
    solo: false,
    warp: 'stretch',
    pitch: 0,
    filter: 'off',
    cutoff: 12000,
    resonance: 1,
    drive: 0,
    sends: { reverb: 0, delay: 0 },
    color: TRACK_COLORS[index % TRACK_COLORS.length],
  };
}

/**
 * Scenes start with no clip entries at all. A missing entry means "follow the
 * track's own pattern", which is what you want while you're still building
 * parts; an explicit entry (including `null` for silence) is a decision you
 * made in the arranger and is never overwritten.
 */
export function makeScene(name: string, bars = 4): Scene {
  return { id: uid('scn'), name, bars, clips: {} };
}

export function createProject(): Project {
  const tracks = Array.from({ length: 4 }, (_, i) => makeTrack(i));
  return {
    version: 1,
    name: 'Untitled',
    bpm: 120,
    beatsPerBar: 4,
    swing: 0,
    sources: [],
    patterns: [],
    tracks,
    scenes: [makeScene('A', 4)],
    master: {
      volume: 0.9,
      ceiling: -1.5,
      reverbSize: 2,
      reverbDecay: 3,
      delayTime: 0.75,
      delayFeedback: 0.35,
      metronome: false,
    },
  };
}
